import React, { useEffect, useState, useMemo } from 'react';
import { Button, Select, notification, Spin, Tag } from 'antd';
import { PlusOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { t } from '../../../i18n/i18n';
import InfoTooltip from '../../InfoTooltip/InfoTooltip';
import { getSubskillsAll, getTestsBySubskills, getTestsByIds } from '../../../API/services/screeningService';
import TreeMultiSelect from '../../TreeMultiSelect/TreeMultiSelect';
import './SubskillsQuestionsCatalog.scss';

const { Option } = Select;

export default function SubskillsQuestionsCatalog({
  onAddQuestion,
  customTestGroup,
}) {
  const [allSubskills, setAllSubskills] = useState([]);
  const [selectedSubskills, setSelectedSubskills] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [loadingSubskills, setLoadingSubskills] = useState(false);
  const [loadingTests, setLoadingTests] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    loadSubskills();
  }, []);

  // Preparar las subskills para TreeMultiSelect
  const subskillsForTree = useMemo(() => {
    return allSubskills.map((subskill) => ({
      id: subskill._id,
      name: subskill.name,
      parentId: subskill?.parent?.id || subskill?.parentId || null,
    }));
  }, [allSubskills]);

  const loadSubskills = async () => {
    setLoadingSubskills(true);
    try {
      const subskills = await getSubskillsAll();
      setAllSubskills(subskills || []);
    } catch (error) {
      console.error('Error loading subskills:', error);
      notification.error({
        message: t('ERROR_LOADING_SUBSKILLS'),
      });
    } finally {
      setLoadingSubskills(false);
    }
  };

  // Cuando cambian las subskills seleccionadas
  const handleSubskillsChange = (selectedIds) => {
    setSelectedSubskills(selectedIds);

    if (selectedIds && selectedIds.length > 0) {
      searchTests(selectedIds);
    } else {
      setAvailableTests([]);
    }
  };

  const searchTests = async (subskillIds) => {
    if (!subskillIds || subskillIds.length === 0) {
      setAvailableTests([]);
      return;
    }

    setLoadingTests(true);
    try {
      const results = await getTestsBySubskills(subskillIds, 30, 1);

      if (results.testIds && results.testIds.length > 0) {
        const testsWithDetails = await getTestsByIds(results.testIds);

        const validTests = testsWithDetails
          .filter((test) => test !== null)
          .map((test) => ({
            ...test,
            testId: test.id || test.testId,
          }));

        setAvailableTests(validTests);
      } else {
        setAvailableTests([]);
        notification.info({
          message: t('NO_TESTS_FOUND_FOR_SUBSKILLS'),
        });
      }
    } catch (error) {
      console.error('Error searching tests by subskills:', error);
      notification.error({
        message: t('ERROR_SEARCHING_TESTS'),
      });
    } finally {
      setLoadingTests(false);
    }
  };

  const handleSelectQuestion = (testId) => {
    const test = availableTests.find((x) => x.id === testId || x.testId === testId);

    const alreadyAdded = customTestGroup?.some((q) => q.testId === testId || q.id === testId);

    if (alreadyAdded) {
      notification.open({
        message: t('CREATE_ADDITIONAL_QUESTIONS_ALREADY_ADDED'),
        type: 'warning',
      });
    } else {
      // Usar el handler unificado con flags para identificar origen
      onAddQuestion([test], {
        fromSubskills: true,
        isReadOnly: true,
      });
    }
  };

  return (
    <div className="subskills-cascade-catalog px-2 rounded-lg mb-3">
      <div className="my-1 my-sm-0 ml-0 d-flex justify-content-end align-items-center">
        <span
          className="text-light-black font-weight-400 text-base my-2 px-3"
          data-testid="subskills-questions-subtitle-span"
        >
          {t('SUBSKILLS_QUESTIONS_SUBTITLE')}
        </span>
        <InfoTooltip
          placement="top"
          content={
            <div className="d-flex flex-column p-2 ml-2">
              <span className="text-xs mb-2">
                {t('SUBSKILLS_QUESTIONS_INFO')}
              </span>
            </div>
          }
        />
      </div>

      <div className="px-3 mr-3 ml-5">
        {/* TreeMultiSelect para subskills jerárquicas */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-end mb-2">
            <div className="d-flex align-items-center">
              <label className="text-sm font-weight-500 mb-0">
                {t('SELECT_SUBSKILLS')}
              </label>
              {selectedSubskills.length > 0 && (
                <Tag color="blue" className="ml-2">{selectedSubskills.length} {t('SELECTED')}</Tag>
              )}
            </div>
            {selectedSubskills.length > 0 && (
              <Button
                type="link"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => handleSubskillsChange([])}
                size="small"
              >
                {t('CLEAR_SELECTION')}
              </Button>
            )}
          </div>
          <TreeMultiSelect
            options={subskillsForTree}
            value={selectedSubskills}
            onChange={handleSubskillsChange}
            className="subskills-tree-select"
            style={{ width: '85%' }}
            label={t('SELECT_SUBSKILLS_PLACEHOLDER')}
            loading={loadingSubskills}
            textSize="12px"
            labelPosition={{ top: 12, left: 10 }}
            width="85%"
          />
        </div>

        {/* Loading Tests */}
        {loadingTests && (
          <div className="d-flex justify-content-center my-4">
            <Spin size="large" tip={t('LOADING_TESTS')} />
          </div>
        )}

        {/* Selector de Tests */}
        {!loadingTests && selectedSubskills.length > 0 && availableTests.length > 0 && (
          <div className="mt-4">
            <label className="text-sm font-weight-500 mb-2 d-block">
              {t('AVAILABLE_TESTS')}{' '}
              <Tag color="success">{availableTests.length}</Tag>
            </label>
            <Select
              placeholder={t('SELECT_QUESTION_PLACEHOLDER')}
              value={null}
              style={{
                width: '85%',
                marginBottom: '1rem',
              }}
              variant="filled"
              open={open}
              onDropdownVisibleChange={(visible) => setOpen(visible)}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {availableTests.map((test) => (
                <Option
                  key={test.id || test.testId}
                  value={test.id || test.testId}
                  className="d-flex border-0 p-0 w-100 cursor-normal"
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="ml-2 w-75">
                      <span
                        className="text-dark font-weight-600 break-word text-truncate d-block"
                        title={test?.text}
                      >
                        {test?.text}
                      </span>
                      <span className="text-xs text-muted">
                        {t(`CREATE_SCREENING_YOUR_QUESTIONS_${test?.answerType}`)}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip className="" placement="top">
                            {t('CREATE_ADDITIONAL_QUESTIONS_ADD')}
                          </Tooltip>
                        }
                      >
                        <Button
                          type="link"
                          className="mr-1 btn-add-question"
                          icon={<PlusOutlined />}
                          data-id={test.id || test.testId}
                          key={test.id || test.testId}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                            handleSelectQuestion(e.currentTarget.dataset.id);
                          }}
                        />
                      </OverlayTrigger>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
